interface EndStreamDetectorOptions {
  newlines?: number;
}

export class EndOfStreamDetector extends TransformStream<Uint8Array, Uint8Array> {
  #options: Required<EndStreamDetectorOptions>;
  #newlineCount = 0;
  #isFinished = false;

  constructor(options: EndStreamDetectorOptions = {}) {
    super({
      transform: (chunk, controller) => {
        if (this.#isFinished) {
          return;
        }

        try {
          const result = this.#checkIsFinished(chunk);

          this.#isFinished = result.end;

          controller.enqueue(result.chunk);

          if (this.#isFinished) {
            controller.terminate();
          }
        } catch {
          // ignore errors
        }
      },
    });

    this.#options = {
      newlines: options.newlines ?? 3,
    };
  }

  isFinished() {
    return this.#isFinished;
  }

  #checkIsFinished(chunk: Uint8Array): { end: boolean; chunk: Uint8Array } {
    let position = 0;

    while (position < chunk.byteLength) {
      if (this.#newlineCount === 0) {
        const newLineIndex = chunk.indexOf(10, position);

        if (newLineIndex === -1) {
          // if we don't find a newline at all, just pass through the chunk
          return { end: false, chunk };
        }

        const count = countNewLines(chunk, newLineIndex);

        if (count >= this.#options.newlines) {
          // if we see the number of newlines we want to see, we are done
          const view = chunk.subarray(0, newLineIndex + count);

          return { end: true, chunk: view };
        }

        if (newLineIndex + count === chunk.byteLength) {
          // if the newlines are at the end, we need to keep track of them for the next chunk
          this.#newlineCount = count;
        }

        position += count + 1;
      } else {
        // if we already have some newlines, we need to check for the newlines at the start of the chunk
        const count = countNewLines(chunk, 0);

        if (count + this.#newlineCount >= this.#options.newlines) {
          const view = chunk.subarray(0, count);

          return { end: true, chunk: view };
        }

        if (count === chunk.byteLength) {
          // if the entire chunk is newlines, we need to keep track of them for the next chunk
          this.#newlineCount += count;
        } else {
          this.#newlineCount = 0;
        }

        position += count;
      }
    }

    return { end: false, chunk };
  }
}

export function countNewLines(chunk: Uint8Array, position: number) {
  let count = 0;

  const end = Math.abs(chunk.byteLength - position);

  for (let i = 0; i < end; i++) {
    if (chunk[position + i] === 10) {
      count++;
    } else {
      break;
    }
  }

  return count;
}
